/**
 * 72.3 step 1 (ARCHITECTURE §5.35; DECISIONS 323, the owner's "Load path + capacity"): what holds a modular building up —
 * read only. Nothing in play calls it yet; step 3 makes it act (collapse). Today nothing checks support: a building whose
 * ground walls are gone stands on nothing, drawn and simulated.
 *
 * - **The support graph**, from the grid alone (memoised per layout). A ground-floor module stands on the ground. A wall or
 *   post on floor f > 0 rests on the wall or post below it, or failing that on the floor plate it stands on (the slabs on
 *   either side of its edge, or round its node). A **plate** is a level's slabs: floor f's tiles (f > 0) and the roof slabs
 *   over floor f − 1, the same height; it rests on the walls and posts of the floor under it, along each slab's edges and
 *   nodes. Furniture, stairs and the like load the tile under them.
 * - **The loads** (constants/modules.ts MODULE_SUPPORT: integer weights and capacities), top down, a floor at a time:
 *   a plate's slabs go to their nearest bearing (a breadth-first pass through the plate from every cell over a standing
 *   bearing, cells in order; the *tributary* rule), split evenly among that cell's bearings (the remainder to the lowest
 *   index); a wall or post passes its weight and what it carries to the wall below, else to its plate.
 * - **Failures:** a bearing carrying more than its capacity; a slab more than SUPPORT_SPAN cells from a bearing, or on a
 *   plate with none; a piece of a plate whose load's middle lies more than SUPPORT_CANTILEVER cells outside its bearings'
 *   extent (it tips), or a slab of it out past that extent by more than the same (a cantilever holds a cell's lip, not a
 *   room); a module with nothing standing under it. **Passes:** every failure of a pass is removed at once and
 *   the building solved again, until a pass finds none (each removes at least one module, so it ends).
 *
 * Pure and exact (integers, fixed orders); memoised on the module-health array as the shapes are (core/moduleShapes.ts),
 * so it is static until a module is hit. Not state. Step 3 (core/collapse.ts) acts on it a pass at a time (`supportPass`, `landingOf`).
 */
import type { StructureState } from '../types';
import type { BuildingLayout, LayoutModule, ModuleKind } from '../types/modules';
import { MODULE_BODY, MODULE_SPAN, MODULE_SUPPORT, SUPPORT_CANTILEVER, SUPPORT_SPAN, WALL_KINDS } from '../constants/modules';

/** Why a module fails: over its capacity, too far through its plate from a bearing, its plate tipping off its bearings, or nothing standing under it. */
export type SupportFailure = 'load' | 'span' | 'tip' | 'unsupported';

export interface SupportSolve {
  /** Each module's carried load (its own weight not counted) as the building stands now — the first pass; 0 for one that carries nothing or is gone. */
  load: readonly number[];
  /** Each module's capacity (MODULE_SUPPORT; 0 for one that is never a bearing). */
  capacity: readonly number[];
  /** The pass a module fails in (1 the first), 0 for one that stands to the end or was destroyed already. */
  failedIn: readonly number[];
  /** Why each failed (null for none). */
  why: readonly (SupportFailure | null)[];
  /** The modules failing in each pass, in index order (empty: the building stands as it is). */
  passes: readonly (readonly number[])[];
}

type Role = 'bearing' | 'slab' | 'load' | 'ground';
interface Graph {
  role: Role[];
  /** A slab's level (a floor tile's floor, a roof slab's floor + 1); a bearing's or a load's floor. */
  level: number[];
  /** A slab's cell index (j × cols + i), else −1. */
  cell: number[];
  /** Per level, the slab in each cell (−1 none). */
  slabs: Map<number, Int32Array>;
  /** Per level ≥ 1, per cell, the bearings under it (walls on its four edges and posts at its four nodes, of the floor below), in index order. */
  under: Map<number, number[][]>;
  /** A bearing's bearing below (the same edge or node a floor down), −1 none. */
  below: number[];
  /** A bearing's or a load's seat on its plate: the slabs it would rest on (by cell), for a bearing whose below is gone or a load on a tile. */
  seat: number[][];
  /** A bearing's extent on the grid, in half cells: [x0, x1, z0, z1] (a wall its run, a post its node). */
  ext: Array<readonly [number, number, number, number]>;
  /** A load with no tile under it (a stair over the opening the flight below climbs from): the stair or ladder below it in its cell, −1 none. */
  onLoad: number[];
  /** The levels top down; the bearings of each floor and the loads on each floor, in index order. */
  top: number;
  bearingsOn: number[][];
  loadsOn: number[][];
  cols: number;
  rows: number;
}

const BEARING_NODE = new Set<ModuleKind>(['post', 'postT']);
const floorOf = (m: LayoutModule): number => m.floor ?? 0;
const spanOf = (k: ModuleKind): number => MODULE_SPAN[k] ?? 1;

/** The cells either side of an edge module (or round a node), in the layout's frame — some may lie outside it. */
function cellsBeside(m: LayoutModule): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  if (m.at === 'edgeX') for (let s = 0; s < spanOf(m.kind); s++) out.push([m.i + s, m.j - 1], [m.i + s, m.j]);
  else if (m.at === 'edgeZ') for (let s = 0; s < spanOf(m.kind); s++) out.push([m.i - 1, m.j + s], [m.i, m.j + s]);
  else if (m.at === 'node') out.push([m.i - 1, m.j - 1], [m.i, m.j - 1], [m.i - 1, m.j], [m.i, m.j]);
  else out.push([m.i, m.j]);
  return out;
}

const graphs = new WeakMap<BuildingLayout, Graph>();
function graphOf(layout: BuildingLayout): Graph {
  const hit = graphs.get(layout);
  if (hit) return hit;
  const n = layout.modules.length, cols = layout.cols, rows = layout.rows;
  const inside = (i: number, j: number) => i >= 0 && j >= 0 && i < cols && j < rows;
  const role: Role[] = new Array(n), level: number[] = new Array(n).fill(0), cell: number[] = new Array(n).fill(-1);
  const slabs = new Map<number, Int32Array>();
  let top = 0;
  const bearingAt = new Map<string, number>();   // `${floor}:${at}:${i}:${j}` → the first bearing there
  layout.modules.forEach((m, k) => {
    const f = floorOf(m);
    top = Math.max(top, f);
    if ((m.at === 'edgeX' || m.at === 'edgeZ') && WALL_KINDS.has(m.kind) || (m.at === 'node' && BEARING_NODE.has(m.kind))) {
      role[k] = 'bearing'; level[k] = f;   // (a ground bearing carries what is over it to the ground)
      const key = `${f}:${m.at}:${m.i}:${m.j}`;
      if (!bearingAt.has(key)) bearingAt.set(key, k);
    } else if (m.at === 'cell' && (m.kind === 'floor' || m.kind === 'roof') && inside(m.i, m.j) && (m.kind === 'roof' || f > 0)) {
      const lv = m.kind === 'roof' ? f + 1 : f;
      role[k] = 'slab'; level[k] = lv; cell[k] = m.j * cols + m.i;
      let row = slabs.get(lv);
      if (!row) { row = new Int32Array(cols * rows).fill(-1); slabs.set(lv, row); }
      if (row[cell[k]] < 0) row[cell[k]] = k;
    } else {
      role[k] = f === 0 ? 'ground' : 'load'; level[k] = f;   // a ground tile, ground furniture, a tower or container on the ground: on the ground
    }
  });
  const below: number[] = new Array(n).fill(-1), seat: number[][] = layout.modules.map(() => []), onLoad: number[] = new Array(n).fill(-1);
  const ext: Array<readonly [number, number, number, number]> = layout.modules.map(m => {
    const s = 2 * spanOf(m.kind);
    return m.at === 'edgeX' ? [2 * m.i, 2 * m.i + s, 2 * m.j, 2 * m.j] : m.at === 'edgeZ' ? [2 * m.i, 2 * m.i, 2 * m.j, 2 * m.j + s] : [2 * m.i, 2 * m.i, 2 * m.j, 2 * m.j];
  });
  const flights = new Map<string, number>();   // `${floor}:${i}:${j}` → the stair or ladder there
  layout.modules.forEach((m, k) => { if ((m.kind === 'stairs' || m.kind === 'ladder') && !flights.has(`${floorOf(m)}:${m.i}:${m.j}`)) flights.set(`${floorOf(m)}:${m.i}:${m.j}`, k); });
  const bearingsOn: number[][] = [], loadsOn: number[][] = [];
  for (let f = 0; f <= top; f++) { bearingsOn.push([]); loadsOn.push([]); }
  const under = new Map<number, number[][]>();
  for (const lv of slabs.keys()) under.set(lv, Array.from({ length: cols * rows }, () => []));
  layout.modules.forEach((m, k) => {
    const f = floorOf(m);
    if (role[k] === 'bearing') {
      bearingsOn[f].push(k);
      if (f > 0) below[k] = bearingAt.get(`${f - 1}:${m.at}:${m.i}:${m.j}`) ?? -1;
      const plate = slabs.get(f), over = under.get(f + 1);
      for (const [i, j] of cellsBeside(m)) {
        if (!inside(i, j)) continue;
        if (f > 0 && plate && plate[j * cols + i] >= 0) seat[k].push(plate[j * cols + i]);
        if (over) over[j * cols + i].push(k);
      }
    } else if (role[k] === 'load') {
      loadsOn[f].push(k);
      const plate = slabs.get(f);
      if (plate && m.at === 'cell' && inside(m.i, m.j) && plate[m.j * cols + m.i] >= 0) seat[k].push(plate[m.j * cols + m.i]);
      else if (m.kind === 'stairs' || m.kind === 'ladder') onLoad[k] = flights.get(`${f - 1}:${m.i}:${m.j}`) ?? -1;
    }
  });
  for (const lists of under.values()) for (const l of lists) l.sort((a, b) => a - b);
  const g: Graph = { role, level, cell, slabs, under, below, seat, ext, onLoad, top, bearingsOn, loadsOn, cols, rows };
  graphs.set(layout, g);
  return g;
}

/** `total` split evenly over `to` (in their order; the remainder one each to the first), added into `carried`. */
function share(carried: number[], to: readonly number[], total: number): void {
  const q = Math.floor(total / to.length), r = total - q * to.length;
  to.forEach((b, i) => { carried[b] += q + (i < r ? 1 : 0); });
}

/** Two ascending index lists merged (no repeats). */
function union(a: readonly number[], b: readonly number[]): number[] {
  const out: number[] = [];
  let i = 0, j = 0;
  while (i < a.length || j < b.length) {
    const x = j >= b.length || (i < a.length && a[i] <= b[j]) ? a[i++] : b[j++];
    if (out[out.length - 1] !== x) out.push(x);
  }
  return out;
}

/** One pass over the standing modules: each one's carried load, and the failures (module → why), in index order. */
function pass(layout: BuildingLayout, g: Graph, up: readonly boolean[], extra?: ReadonlyArray<readonly [number, number]>): { carried: number[]; fails: Map<number, SupportFailure> } {
  const carried: number[] = new Array(layout.modules.length).fill(0);
  if (extra) for (const [k, v] of extra) if (up[k]) carried[k] += v;   // (72.3 step 3: what has landed on a slab)
  const fails = new Map<number, SupportFailure>();
  const w = (k: number) => MODULE_SUPPORT[layout.modules[k].kind].weight;
  const cap = (k: number) => MODULE_SUPPORT[layout.modules[k].kind].capacity;
  const levelTop = Math.max(g.top + 1, ...g.slabs.keys());
  for (let lv = levelTop; lv >= 0; lv--) {
    if (lv <= g.top) {
      // the bearings of this floor (carrying the plate over them and the walls above): down to the bearing below, else onto
      // the plate they stand on; over capacity, they fail
      for (const k of g.bearingsOn[lv]) {
        if (!up[k]) continue;
        if (carried[k] > cap(k)) { fails.set(k, 'load'); continue; }
        if (lv === 0) continue;   // (the ground)
        const b = g.below[k];
        if (b >= 0 && up[b]) { carried[b] += w(k) + carried[k]; continue; }
        const seats = g.seat[k].filter(s => up[s]);
        if (seats.length === 0) { fails.set(k, 'unsupported'); continue; }
        share(carried, seats, w(k) + carried[k]);
      }
      // the loads on this floor: onto the tile under them
      for (const k of g.loadsOn[lv]) {
        if (!up[k]) continue;
        const seats = g.seat[k].filter(s => up[s]), on = g.onLoad[k];
        if (seats.length) share(carried, seats, w(k) + carried[k]);
        else if (on >= 0 && up[on]) { if (g.role[on] === 'load') carried[on] += w(k) + carried[k]; }   // (on a ground flight: on the ground)
        else fails.set(k, 'unsupported');
      }
    }
    // the plate at this level (with what stands on it), onto the bearings of the floor under it
    const plate = lv > 0 ? g.slabs.get(lv) : undefined, under = g.under.get(lv);
    if (!plate || !under) continue;
    const cells = g.cols * g.rows, dist = new Int32Array(cells).fill(-1);
    const standingUnder = (c: number) => under[c].filter(b => up[b]);
    // each slab's nearest bearings: those under every source cell at its distance through the plate (ties shared)
    const near: number[][] = Array.from({ length: cells }, () => []);
    const queue: number[] = [];
    for (let c = 0; c < cells; c++) if (plate[c] >= 0 && up[plate[c]]) { const b = standingUnder(c); if (b.length) { dist[c] = 0; near[c] = b; queue.push(c); } }
    for (let h = 0; h < queue.length; h++) {
      const c = queue[h], i = c % g.cols, j = (c - i) / g.cols;
      for (const [di, dj] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
        const ni = i + di, nj = j + dj;
        if (ni < 0 || nj < 0 || ni >= g.cols || nj >= g.rows) continue;
        const nc = nj * g.cols + ni;
        if (plate[nc] < 0 || !up[plate[nc]]) continue;
        if (dist[nc] < 0) { dist[nc] = dist[c] + 1; queue.push(nc); }
        if (dist[nc] === dist[c] + 1) near[nc] = union(near[nc], near[c]);
      }
    }
    // each piece of the plate (its standing slabs joined by their edges) tips when the middle of its load lies more than
    // SUPPORT_CANTILEVER cells outside its bearings' extent: a plate on one wall line holds only a cell's lip
    const piece = new Int32Array(cells).fill(-1), tipped: boolean[] = [], box: Array<readonly [number, number, number, number]> = [];
    for (let c0 = 0; c0 < cells; c0++) {
      if (plate[c0] < 0 || !up[plate[c0]] || piece[c0] >= 0) continue;
      const id = tipped.length, members = [c0];
      piece[c0] = id;
      let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity, sum = 0, sx = 0, sz = 0;
      for (let h = 0; h < members.length; h++) {
        const c = members[h], i = c % g.cols, j = (c - i) / g.cols, k = plate[c], l = w(k) + carried[k];
        sum += l; sx += l * (2 * i + 1); sz += l * (2 * j + 1);
        for (const b of under[c]) if (up[b]) { const e = g.ext[b]; x0 = Math.min(x0, e[0]); x1 = Math.max(x1, e[1]); z0 = Math.min(z0, e[2]); z1 = Math.max(z1, e[3]); }
        for (const [di, dj] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
          const ni = i + di, nj = j + dj, nc = nj * g.cols + ni;
          if (ni < 0 || nj < 0 || ni >= g.cols || nj >= g.rows || plate[nc] < 0 || !up[plate[nc]] || piece[nc] >= 0) continue;
          piece[nc] = id; members.push(nc);
        }
      }
      const m = 2 * SUPPORT_CANTILEVER;   // (the load's middle, sx / sum, against the extent: compared multiplied through, exact)
      tipped.push(x0 !== Infinity && (sx < (x0 - m) * sum || sx > (x1 + m) * sum || sz < (z0 - m) * sum || sz > (z1 + m) * sum));
      box.push([x0 - m, x1 + m, z0 - m, z1 + m]);
    }
    for (let c = 0; c < cells; c++) {
      const k = plate[c];
      if (k < 0 || !up[k]) continue;
      if (dist[c] < 0) { fails.set(k, 'unsupported'); continue; }
      if (tipped[piece[c]]) { fails.set(k, 'tip'); continue; }
      const i = c % g.cols, j = (c - i) / g.cols, b = box[piece[c]];
      if (2 * i + 1 < b[0] || 2 * i + 1 > b[1] || 2 * j + 1 < b[2] || 2 * j + 1 > b[3]) { fails.set(k, 'span'); continue; }   // (out past its bearings by more than a cell's lip)
      if (dist[c] > SUPPORT_SPAN) { fails.set(k, 'span'); continue; }
      share(carried, near[c], w(k) + carried[k]);
    }
    // 72.3 step 3's fix: a lip (a slab out past its bearings' extent, held by the cantilever) carries no bearing — a wall or post
    // standing on its plate with nothing under it, every standing seat a lip, fails; else lips stack (the upper walls on the
    // floor's lip, the roof's lip off them: two cells out past the last bearing to the ground)
    if (lv > g.top) continue;
    const lip = (s: number): boolean => {
      const c = g.cell[s], i = c % g.cols, j = (c - i) / g.cols, b = box[piece[c]], m = 2 * SUPPORT_CANTILEVER;
      return 2 * i + 1 < b[0] + m || 2 * i + 1 > b[1] - m || 2 * j + 1 < b[2] + m || 2 * j + 1 > b[3] - m;
    };
    for (const k of g.bearingsOn[lv]) {
      if (!up[k] || fails.has(k)) continue;
      const b = g.below[k];
      if (b >= 0 && up[b]) continue;
      const seats = g.seat[k].filter(s => up[s] && !fails.has(s));
      if (seats.length && seats.every(lip)) fails.set(k, 'tip');
    }
  }
  return { carried, fails };
}

const memo = new WeakMap<BuildingLayout, SupportSolve>();
const wornMemo = new WeakMap<readonly number[], SupportSolve>();

/**
 * What holds `layout` up with these module healths (absent: all whole) — the loads as it stands, and what would fail, pass by
 * pass. Memoised per layout (whole) or per health array (as the shapes are).
 */
export function supportSolve(layout: BuildingLayout, modules?: readonly number[]): SupportSolve {
  const hit = modules ? wornMemo.get(modules) : memo.get(layout);
  if (hit) return hit;
  const g = graphOf(layout), n = layout.modules.length;
  const up = layout.modules.map((m, k) => (modules?.[k] ?? MODULE_BODY[m.kind].health) > 0);
  const failedIn: number[] = new Array(n).fill(0), why: (SupportFailure | null)[] = new Array(n).fill(null), passes: number[][] = [];
  let load: number[] | null = null;
  for (let p = 1; p <= n; p++) {
    const { carried, fails } = pass(layout, g, up);
    load ??= carried;
    if (fails.size === 0) break;
    const ks = [...fails.keys()].sort((a, b) => a - b);
    for (const k of ks) { up[k] = false; failedIn[k] = p; why[k] = fails.get(k)!; }
    passes.push(ks);
  }
  const out: SupportSolve = { load: load ?? new Array(n).fill(0), capacity: layout.modules.map(m => MODULE_SUPPORT[m.kind].capacity), failedIn, why, passes };
  if (modules) wornMemo.set(modules, out); else memo.set(layout, out);
  return out;
}

/**
 * 72.3 step 3 (core/collapse.ts): one pass over the building as it stands, with `extra` load on some modules (what has landed on
 * a slab, × COLLAPSE.impact) — the modules that fail now, in index order. Not memoised: a collapse runs it a few times.
 */
export function supportPass(layout: BuildingLayout, modules: readonly number[], extra?: ReadonlyArray<readonly [number, number]>): number[] {
  const up = layout.modules.map((m, k) => (modules[k] ?? MODULE_BODY[m.kind].health) > 0);
  return [...pass(layout, graphOf(layout), up, extra).fails.keys()].sort((a, b) => a - b);
}

/**
 * 72.3 step 3: where module `k` comes to rest falling straight down its column, with `up` standing — the standing slabs of the
 * highest plate under it (a bearing or a load: its own floor's plate beside it and down; a slab: its own cell, lower plates), in
 * index order, and that plate's level; none and level 0: the ground.
 */
export function landingOf(layout: BuildingLayout, up: readonly boolean[], k: number): { onto: number[]; level: number } {
  const g = graphOf(layout), m = layout.modules[k];
  const from = g.role[k] === 'slab' ? g.level[k] - 1 : floorOf(m);
  const cells = cellsBeside(m).filter(([i, j]) => i >= 0 && j >= 0 && i < g.cols && j < g.rows).map(([i, j]) => j * g.cols + i);
  for (let lv = from; lv >= 1; lv--) {
    const plate = g.slabs.get(lv);
    if (!plate) continue;
    const onto = [...new Set(cells.map(c => plate[c]).filter(s => s >= 0 && s !== k && up[s]))].sort((a, b) => a - b);
    if (onto.length) return { onto, level: lv };
  }
  return { onto: [], level: 0 };
}

/** 72.3 step 3: the slabs module `k` stands on (an upper floor's bearing or load: its plate's slabs beside it; else none — the ground). */
export function seatOf(layout: BuildingLayout, k: number): readonly number[] { return graphOf(layout).seat[k]; }

/** A structure's support as it stands (its layout and its module healths). */
export function structureSupport(s: Pick<StructureState, 'modules'>, layout: BuildingLayout): SupportSolve {
  return supportSolve(layout, s.modules);
}
